import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Plus, Check, X, User } from 'lucide-react';
import { listPessoas, Pessoa } from '../api/pessoa';
import { formatCNPJ } from '../utils/formatters';

interface Props {
  label: string;
  value: number | string | null;
  onChange: (id: number | null, pessoa?: Pessoa | null) => void;
  placeholder?: string;
  required?: boolean;
  optional?: boolean;
  className?: string;
}

function fmtDoc(p: Pessoa): string {
  if (!p.documento) return '';
  const doc = String(p.documento);
  if (doc.length === 14) return formatCNPJ(doc);
  if (doc.length === 11) return `${doc.slice(0, 3)}.${doc.slice(3, 6)}.${doc.slice(6, 9)}-${doc.slice(9)}`;
  return doc;
}

function pessoaMeta(p: Pessoa): string {
  return [fmtDoc(p), p.uf, p.xmun].filter(Boolean).join(' / ');
}

export default function PessoaAutocomplete({ label, value, onChange, placeholder, required, optional, className }: Props) {
  const { data: pessoas = [], isLoading } = useQuery({
    queryKey: ['pessoas'],
    queryFn: listPessoas,
    staleTime: 60_000,
  });

  const selectedId = value ? Number(value) : null;
  const selectedPessoa = useMemo(() => pessoas.find((p) => p.id === selectedId) || null, [pessoas, selectedId]);

  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (q.length < 3) return [];
    return pessoas
      .filter((p) => [p.nome, p.nome_fantasia, p.documento].some((v) => String(v || '').toLowerCase().includes(q)))
      .slice(0, 30);
  }, [pessoas, busca]);

  function selecionar(p: Pessoa | null) {
    onChange(p?.id ?? null, p);
    setOpen(false);
    setBusca('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      setHighlight((h) => Math.min(h + 1, filtradas.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlight((h) => Math.max(h - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter') {
      if (filtradas[highlight]) {
        selecionar(filtradas[highlight]);
        e.preventDefault();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className={className ?? ''} ref={containerRef}>
      <label className="field-label">
        {label}
        {optional && <span className="font-normal normal-case text-neutral-500 dark:text-neutral-400"> (opcional)</span>}
        {required && <span className="text-rose-700 dark:text-rose-400"> *</span>}
      </label>

      {selectedPessoa ? (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm dark:border-emerald-500/30 dark:bg-emerald-500/5">
          <User className="h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-400" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-slate-950 dark:text-white">
              {selectedPessoa.nome}
              {selectedPessoa.nome_fantasia && selectedPessoa.nome_fantasia !== selectedPessoa.nome && (
                <span className="ml-2 text-xs font-normal text-neutral-600 dark:text-neutral-400">({selectedPessoa.nome_fantasia})</span>
              )}
            </div>
            <div className="mono truncate text-xs text-neutral-600 dark:text-neutral-400">
              {pessoaMeta(selectedPessoa)}
            </div>
          </div>
          <Link
            to={`/pessoas/${selectedPessoa.id}`}
            target="_blank"
            className="rounded px-2 py-1 text-xs text-neutral-600 hover:bg-slate-100 hover:text-slate-950 dark:text-neutral-400 dark:hover:bg-white/5 dark:hover:text-white"
            title="Editar pessoa"
          >
            Editar
          </Link>
          <button
            type="button"
            onClick={() => selecionar(null)}
            className="rounded p-1 text-neutral-500 hover:bg-slate-100 hover:text-rose-700 dark:text-neutral-400 dark:hover:bg-white/5 dark:hover:text-rose-400"
            title="Remover seleção"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <input
            type="search"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setOpen(true);
              setHighlight(0);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder ?? 'Buscar por nome ou CNPJ/CPF, mínimo 3 letras'}
            className="field-input pl-9"
            autoComplete="off"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-neutral-500">
              carregando...
            </div>
          )}

          {open && (
            <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
              {busca.trim().length < 3 ? (
                <div className="p-4 text-center text-sm text-neutral-600 dark:text-neutral-400">
                  Digite pelo menos 3 letras para buscar.
                </div>
              ) : filtradas.length === 0 ? (
                <div className="p-4 text-center">
                  <div className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">
                    Nenhuma pessoa encontrada para "{busca}".
                  </div>
                  <Link to="/pessoas/novo" target="_blank" className="btn-primary inline-flex text-xs !py-1.5">
                    <Plus className="h-3.5 w-3.5" /> Cadastrar nova pessoa
                  </Link>
                </div>
              ) : (
                <ul>
                  {filtradas.map((p, i) => {
                    const isSel = p.id === selectedId;
                    const isHi = i === highlight;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selecionar(p);
                          }}
                          onMouseEnter={() => setHighlight(i)}
                          className={`flex w-full items-start gap-2 border-b border-slate-200 px-3 py-2.5 text-left text-sm transition last:border-b-0 dark:border-slate-800 ${
                            isHi ? 'bg-emerald-50 dark:bg-emerald-500/10' : 'hover:bg-slate-100 dark:hover:bg-slate-900'
                          }`}
                        >
                          <span className={`w-4 shrink-0 pt-0.5 ${isSel ? 'text-emerald-700 dark:text-emerald-400' : 'text-transparent'}`}>
                            <Check className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-semibold text-slate-950 dark:text-white">
                              {p.nome}
                              {p.nome_fantasia && p.nome_fantasia !== p.nome && (
                                <span className="ml-2 text-xs font-normal text-neutral-600 dark:text-neutral-400">({p.nome_fantasia})</span>
                              )}
                            </span>
                            <span className="mono block truncate text-xs text-neutral-600 dark:text-neutral-400">
                              {pessoaMeta(p)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                  <li className="sticky bottom-0 border-t border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-ink-900">
                    <Link to="/pessoas/novo" target="_blank" className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-slate-100 dark:text-emerald-400 dark:hover:bg-white/5">
                      <Plus className="h-4 w-4" /> Cadastrar nova pessoa
                    </Link>
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
